import { useState, useEffect, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { billingApi } from '@/api/clients/apiClient'

export default function Budget() {
  const { orgId } = useAuthStore()
  const queryClient = useQueryClient()
  const [successMsg, setSuccessMsg] = useState('')

  // Budget form state
  const [periodType, setPeriodType] = useState<'monthly' | 'daily'>('monthly')
  const [limitUSD, setLimitUSD] = useState('0')
  const [alertPct, setAlertPct] = useState('80')

  const { data: budgetResp, isLoading: loadingBudget } = useQuery({
    queryKey: ['budget', orgId],
    queryFn: () => billingApi.getBudget(orgId!),
    enabled: !!orgId,
  })

  const { data: pricingResp, isLoading: loadingPricing } = useQuery({
    queryKey: ['pricing', orgId],
    queryFn: () => billingApi.getPricing(orgId!),
    enabled: !!orgId,
  })

  // Populate form when budget loads
  useEffect(() => {
    const budget = budgetResp?.data
    if (budget) {
      setPeriodType(budget.PeriodType)
      setLimitUSD(String(budget.LimitUSD))
      setAlertPct(String(budget.AlertPct))
    }
  }, [budgetResp])

  const saveMutation = useMutation({
    mutationFn: () =>
      billingApi.upsertBudget(orgId!, {
        period_type: periodType,
        limit_usd: parseFloat(limitUSD) || 0,
        alert_pct: parseFloat(alertPct) || 80,
      }),
    onSuccess: () => {
      setSuccessMsg('Budget saved.')
      setTimeout(() => setSuccessMsg(''), 3000)
      queryClient.invalidateQueries({ queryKey: ['budget', orgId] })
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    saveMutation.mutate()
  }

  const budget = budgetResp?.data
  const pricing = pricingResp?.data ?? []

  if (loadingBudget || loadingPricing) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="material-icons animate-spin text-primary text-4xl">sync</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">Budget</h1>
        <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
          Spending limits and LLM pricing reference
        </p>
      </div>

      {/* Budget config */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark p-6">
        <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">
          Spending Limit
        </h2>

        {successMsg && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
            <span className="material-icons text-success text-sm">check_circle</span>
            <p className="text-sm text-green-700 dark:text-green-300">{successMsg}</p>
          </div>
        )}

        {saveMutation.isError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-300">
              {(saveMutation.error as Error)?.message ?? 'Failed to save budget.'}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
              Period
            </label>
            <select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as 'monthly' | 'daily')}
              className="w-full px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="monthly">Monthly</option>
              <option value="daily">Daily</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
              Limit (USD)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle-light dark:text-subtle-dark text-sm">
                $
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={limitUSD}
                onChange={(e) => setLimitUSD(e.target.value)}
                className="w-full pl-7 pr-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="0.00"
              />
            </div>
            <p className="text-xs text-subtle-light dark:text-subtle-dark mt-1">
              Set to 0 for unlimited.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
              Alert threshold (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={alertPct}
              onChange={(e) => setAlertPct(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <p className="text-xs text-subtle-light dark:text-subtle-dark mt-1">
              Alert when usage reaches this % of the limit.
            </p>
          </div>

          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-60 text-sm font-medium"
          >
            {saveMutation.isPending && (
              <span className="material-icons text-base animate-spin">sync</span>
            )}
            Save budget
          </button>
        </form>

        {/* Current values summary */}
        {budget && (
          <div className="mt-6 pt-4 border-t border-border-light dark:border-border-dark">
            <p className="text-xs text-subtle-light dark:text-subtle-dark uppercase tracking-wider mb-2">
              Current saved budget
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-text-light dark:text-text-dark">
              <span>
                Period: <strong className="capitalize">{budget.PeriodType}</strong>
              </span>
              <span>
                Limit:{' '}
                <strong>{budget.LimitUSD === 0 ? 'Unlimited' : `$${budget.LimitUSD}`}</strong>
              </span>
              <span>
                Alert at: <strong>{budget.AlertPct}%</strong>
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Pricing table */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark overflow-hidden">
        <div className="px-6 py-4 border-b border-border-light dark:border-border-dark">
          <h2 className="text-base font-semibold text-text-light dark:text-text-dark">
            LLM Pricing Reference
          </h2>
          <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5">
            USD per 1 million tokens
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-light dark:bg-background-dark">
              <tr>
                <th className="px-6 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Model
                </th>
                <th className="px-6 py-3 text-right font-medium text-subtle-light dark:text-subtle-dark">
                  Input ($/M tokens)
                </th>
                <th className="px-6 py-3 text-right font-medium text-subtle-light dark:text-subtle-dark">
                  Output ($/M tokens)
                </th>
              </tr>
            </thead>
            <tbody>
              {pricing
                .slice()
                .sort((a, b) => a.model.localeCompare(b.model))
                .map((entry) => (
                  <tr
                    key={entry.model}
                    className="border-t border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark"
                  >
                    <td className="px-6 py-3 font-mono text-xs text-text-light dark:text-text-dark">
                      {entry.model}
                    </td>
                    <td className="px-6 py-3 text-right text-text-light dark:text-text-dark">
                      ${entry.input_per_m_tokens_usd.toFixed(2)}
                    </td>
                    <td className="px-6 py-3 text-right text-text-light dark:text-text-dark">
                      ${entry.output_per_m_tokens_usd.toFixed(2)}
                    </td>
                  </tr>
                ))}

              {pricing.length === 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="px-6 py-8 text-center text-subtle-light dark:text-subtle-dark"
                  >
                    No pricing data available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
